/**
 * Resize an image file to a maximum dimension while maintaining aspect ratio
 * @param {File} file - The image file to resize
 * @param {Object} options - Resize options
 * @param {number} options.maxWidth - Maximum width in pixels (default: 1920)
 * @param {number} options.maxHeight - Maximum height in pixels (default: 1080)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.85)
 * @param {string} options.outputType - Output MIME type (default: 'image/jpeg')
 * @returns {Promise<File>} - Resized image file
 */
export async function resizeImage(file, options = {}) {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.85,
    outputType = 'image/jpeg'
  } = options

  return new Promise((resolve, reject) => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    img.onload = () => {
      let { width, height } = img

      // Calculate new dimensions maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height)
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)
      }

      canvas.width = width
      canvas.height = height

      // Draw image with white background (for transparency)
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, 0, width, height)
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to resize image'))
            return
          }

          // Create new file with resized blob
          const resizedFile = new File([blob], file.name, {
            type: outputType,
            lastModified: Date.now()
          })

          resolve(resizedFile)
        },
        outputType,
        quality
      )
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    // Load image from file
    img.src = URL.createObjectURL(file)
  })
}

/**
 * Resize an image for club covers (16:9 aspect, max 1920x1080)
 */
export async function resizeClubCover(file) {
  return resizeImage(file, {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.85
  })
}

/**
 * Resize an image for club posts (max 1920x1920)
 */
export async function resizeClubPostImage(file) {
  return resizeImage(file, {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.85
  })
}
